// packages/web-ui/src/AppShell/ContextPanel-modes/ViewsMode.tsx
//
// Views destination context-panel body. Renders the saved-views list pulled
// from `GET /api/v1/views`. The status hook
// (`useContextPanelStatus('views')`) derives loading / ready / error / idle
// from the views slice; this component owns the mount-time fetch and the
// list-row presentation.
//
// Row layout:
//   - 36×36 SVG thumbnail (lazy-loaded via the existing
//     `/api/v1/views/<id>/export.svg` endpoint)
//   - name + lastModified (relative)
//   - the row click sets `activeViewId` so the full-canvas Views editor can
//     mount the layout
//
// The empty-state and error retry are still rendered by ModeChrome — only
// the populated body is owned here.
//
// A populated list also renders a small toolbar with a destructive
// "Delete all" button that calls `viewsSlice.deleteAllViews` after the user
// confirms in an inline alertdialog. The toolbar is intentionally only
// visible when there is at least one stored view (nothing to delete
// otherwise).

import { useEffect, useCallback, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { ModeChrome } from './ModeChrome.js';
import { useContextPanelStatus } from './useContextPanelStatus.js';
import { useRootStore } from '../../state/store.js';
import type { ViewSummary } from '../../state/views.js';
import { navigateTo } from '../../router/hash-router.js';

export function ViewsMode(): JSX.Element {
  const status = useContextPanelStatus('views');
  const viewsList = useRootStore((s) => s.viewsList);
  const activeViewId = useRootStore((s) => s.activeViewId);
  const loadViewsList = useRootStore((s) => s.loadViewsList);
  const setActiveViewId = useRootStore((s) => s.setActiveViewId);
  const deleteAllViews = useRootStore((s) => s.deleteAllViews);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Mount-time fetch. The slice's `loadViewsList` sets `loading=true` so the
  // status hook flips to `'loading'` and the chrome shows skeleton rows.
  useEffect(() => {
    void loadViewsList();
  }, [loadViewsList]);

  const handleRowClick = useCallback(
    (viewId: string) => {
      setActiveViewId(viewId);
      navigateTo({ kind: 'views', viewId });
    },
    [setActiveViewId],
  );

  const handleConfirmDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteAllViews();
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  }, [deleteAllViews]);

  const populatedBody = viewsList.length > 0 ? (
    <div className="flex h-full flex-col">
      <div
        data-testid="views-mode-toolbar"
        className="flex items-center justify-end border-b border-taupe-300 bg-cream-100 px-2 py-1"
      >
        <button
          type="button"
          data-testid="views-mode-delete-all"
          onClick={() => {
            setConfirmOpen(true);
          }}
          className="text-audi-red hover:bg-cream-200 rounded border border-taupe-500 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
        >
          Delete all
        </button>
      </div>
      <ul data-testid="views-mode-list" className="flex flex-col">
        {viewsList.map((v) => (
          <ViewRow
            key={v.viewId}
            summary={v}
            active={v.viewId === activeViewId}
            onClick={handleRowClick}
          />
        ))}
      </ul>
    </div>
  ) : undefined;

  return (
    <>
      <ModeChrome
        modeKey="views"
        testId="views-mode-root"
        title="Views"
        ctaLabel="+ New view from chat"
        onCtaClick={() => {
          navigateTo({ kind: 'chat' });
        }}
        emptyStateText="No saved views. Generate one via chat: /generate-view"
        errorText="Could not load saved views."
        status={status}
      >
        {populatedBody}
      </ModeChrome>
      <DeleteAllConfirmDialog
        open={confirmOpen}
        viewCount={viewsList.length}
        isDeleting={deleting}
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
      />
    </>
  );
}

interface ViewRowProps {
  summary: ViewSummary;
  active: boolean;
  onClick: (viewId: string) => void;
}

function ViewRow({ summary, active, onClick }: ViewRowProps): JSX.Element {
  const activeClass = active ? 'bg-cream-200' : 'hover:bg-cream-200';
  return (
    <li>
      <button
        type="button"
        data-testid={`views-mode-row-${summary.viewId}`}
        onClick={() => {
          onClick(summary.viewId);
        }}
        className={`flex w-full items-center gap-2 border-b border-taupe-300 px-2 py-2 text-left text-xs ${activeClass}`}
      >
        <img
          src={summary.thumbnailUrl}
          alt=""
          width={36}
          height={36}
          loading="lazy"
          className="h-9 w-9 flex-shrink-0 rounded border border-taupe-300 bg-cream-50 object-contain"
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium text-taupe-900" title={summary.viewName}>
            {summary.viewName}
          </span>
          <span className="text-[10px] text-taupe-700">
            {formatRelativeIso(summary.lastModified)}
          </span>
        </span>
      </button>
    </li>
  );
}

interface DeleteAllConfirmDialogProps {
  open: boolean;
  viewCount: number;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteAllConfirmDialog({
  open,
  viewCount,
  isDeleting,
  onCancel,
  onConfirm,
}: DeleteAllConfirmDialogProps): JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const heading =
    viewCount === 1
      ? 'Permanently delete the saved view?'
      : `Permanently delete all ${String(viewCount)} saved views?`;

  // Portal to <body>: the ContextPanel `<aside>` is a flex item with an
  // explicit `zIndex`, which creates a local stacking context. A `position:
  // fixed` descendant inside that context still resolves its layout against
  // the viewport, but its paint order is scoped to the aside — at the same
  // parent z-index the later sibling (TabContentArea) wins and obscures the
  // dialog. Portalling to <body> sidesteps the trap.
  const dialog = (
    <div
      data-testid="views-delete-all-dialog"
      className="fixed inset-0 flex items-center justify-center bg-black/60"
      style={{ zIndex: 100 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="views-delete-all-heading"
        aria-describedby="views-delete-all-body"
        className="bg-cream-50 border-taupe-500 mx-4 w-full max-w-md rounded-none border p-5"
      >
        <h2
          id="views-delete-all-heading"
          data-testid="views-delete-all-dialog-heading"
          className="text-fg text-base font-extrabold tracking-[-0.01em]"
        >
          {heading}
        </h2>
        <p
          id="views-delete-all-body"
          data-testid="views-delete-all-dialog-body"
          className="text-taupe-700 mt-2 text-sm"
        >
          This cannot be undone. Every saved view file will be removed from disk.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            data-testid="views-delete-all-dialog-cancel"
            className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 text-fg rounded-none border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="views-delete-all-dialog-confirm"
            className="bg-audi-red hover:bg-audi-red-hover rounded-none px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white disabled:opacity-60"
            onClick={onConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting ? 'true' : undefined}
          >
            {isDeleting ? 'Deleting…' : 'Delete all'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}

/**
 * Format an ISO-8601 timestamp into a short relative string ("3m", "2h",
 * "5d"). Returns the locale date for anything older than ~7 days. Kept
 * inline because there's no shared util for this in `web-ui/` yet and a
 * single-purpose helper does not need its own module.
 */
function formatRelativeIso(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const deltaSec = Math.floor((Date.now() - t) / 1000);
  if (deltaSec < 60) return 'just now';
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${String(min)}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${String(hr)}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${String(day)}d ago`;
  return new Date(t).toLocaleDateString();
}
